import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { api } from '@/lib/api-client';

function rethrowWithApiErrorMessage(error: unknown, fallback: string): never {
    if (isAxiosError(error)) {
        const data = error.response?.data;
        if (data && typeof data === 'object' && data !== null && 'error' in data) {
            const msg = (data as { error: unknown }).error;
            if (typeof msg === 'string' && msg.trim() !== '') {
                throw new Error(msg);
            }
        }
    }
    if (error instanceof Error) {
        throw error;
    }
    throw new Error(fallback);
}

export function useEntityCreate(entityType: string, appSlug?: string) {
    const queryClient = useQueryClient();
    const basePath = appSlug ? `/applications/${appSlug}/${entityType}` : `/${entityType}`;

    return useMutation({
        mutationFn: (data: Record<string, unknown>) =>
            api.post(basePath, data).then((r) => r.data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [entityType] }),
    });
}

export function useEntityUpdate(entityType: string, id: string, appSlug?: string) {
    const queryClient = useQueryClient();
    const basePath = appSlug
        ? `/applications/${appSlug}/${entityType}/${id}`
        : `/${entityType}/${id}`;

    return useMutation({
        mutationFn: (data: Record<string, unknown>) =>
            api.put(basePath, data).then((r) => r.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [entityType, id] });
            queryClient.invalidateQueries({ queryKey: [entityType] });
        },
    });
}

export function useEntityArchive(entityType: string, id: string, appSlug?: string) {
    const queryClient = useQueryClient();
    const basePath = appSlug
        ? `/applications/${appSlug}/${entityType}/${id}`
        : `/${entityType}/${id}`;

    return useMutation({
        mutationFn: () => api.delete(basePath).then((r) => r.data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [entityType] }),
    });
}

export function useCapabilityCreate(appKey: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: Record<string, unknown>) => {
            try {
                const r = await api.post(`/applications/${appKey}/capabilities`, data);
                return r.data;
            } catch (e) {
                rethrowWithApiErrorMessage(e, 'Unable to create capability.');
            }
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['capabilities'] }),
    });
}

export function useCapabilityUpdate(appKey: string, name: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: Record<string, unknown>) =>
            api
                .put(`/applications/${appKey}/capabilities/${encodeURIComponent(name)}`, data)
                .then((r) => r.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['capability', appKey, name] });
            queryClient.invalidateQueries({ queryKey: ['capabilities'] });
            queryClient.invalidateQueries({ queryKey: ['editor-bundle', appKey, name] });
        },
    });
}

export function usePublishRevision(appKey: string, name: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: { rev_id: string; major: number; minor: number; patch: number }) =>
            api
                .post(
                    `/applications/${appKey}/capabilities/${name}/revisions/${data.rev_id}/publish`,
                    data,
                )
                .then((r) => r.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['capability', appKey, name] });
            queryClient.invalidateQueries({ queryKey: ['revisions', appKey, name] });
        },
    });
}
